/**
 * RestConnectPanel - the REST connector's connect experience.
 *
 * Replaces the old "one auth header behind Advanced" with:
 *   • Popular-API presets (Alpaca, CoinGecko, Polygon, FRED, GitHub...) that
 *     pre-fill URL, auth scheme, pagination and the incremental param - the
 *     user only pastes keys.
 *   • Smart paste: drop a `curl ...` command or a raw HTTP request into the
 *     URL box and method/headers/auth/body fill themselves (handled by
 *     ConnectData; this panel renders the confirmation).
 *   • Every real auth scheme: Bearer, single API key (header OR query
 *     param), MULTIPLE secret headers (Alpaca-style), Basic, and OAuth2
 *     client-credentials.
 *   • Query params editor (synced with the URL), extra headers, POST body.
 */

import React from 'react';
import {
  Box, Typography, TextField, Stack, MenuItem, IconButton,
  Button, Link, Tooltip, Autocomplete, useTheme, alpha,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import {
  REST_PRESETS, getUrlParams, setUrlParams,
} from './restPresets';

// Search matches the label, provider group, and each preset's keyword list
// (synonyms like "ohlc", "candles", tickers, provider names...).
const presetFilter = createFilterOptions({
  stringify: (p) => `${p.label} ${p.group} ${p.keywords || ''}`,
});

const FIELD = { fullWidth: true, size: 'small', sx: { '& .MuiInputBase-input': { fontSize: 13 } } };

const AUTH_OPTIONS = [
  ['none', 'None (public API)'],
  ['bearer', 'Bearer token'],
  ['apikey', 'API key'],
  ['headers', 'Multiple headers'],
  ['basic', 'Basic (user / pass)'],
  ['oauth2_client_credentials', 'OAuth2 (client credentials)'],
];

// ---- small key/value row editor -------------------------------------------
function KeyValueRows({ rows, onChange, keyLabel = 'Name', valueLabel = 'Value', secretValues = false, addLabel = 'Add' }) {
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { name: '', value: '' }]);

  return (
    <Stack spacing={1}>
      {rows.map((r, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Stack key={i} direction="row" spacing={1} alignItems="center">
          <TextField {...FIELD} label={keyLabel} value={r.name ?? r.key ?? ''}
            onChange={(e) => update(i, r.key !== undefined ? { key: e.target.value } : { name: e.target.value })} />
          <TextField {...FIELD} label={valueLabel} type={secretValues ? 'password' : 'text'}
            value={r.value || ''} onChange={(e) => update(i, { value: e.target.value })} />
          <IconButton size="small" onClick={() => remove(i)} aria-label="remove row">
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      ))}
      <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={add}
        sx={{ textTransform: 'none', alignSelf: 'flex-start', color: 'text.secondary' }}>
        {addLabel}
      </Button>
    </Stack>
  );
}

export default function RestConnectPanel({ rest, setRest, connString, setConnString, smartPaste, onApplyPreset }) {
  const theme = useTheme();
  const set = (patch) => setRest((r) => ({ ...r, ...patch }));
  const preset = REST_PRESETS.find((p) => p.id === rest.presetId) || null;

  // Query-param rows are a local draft synced with the URL: edits here
  // rewrite the URL, and external URL changes (preset / smart paste / typing)
  // re-derive the rows. A draft keeps just-added empty rows visible while
  // the user types the param name.
  const [paramRows, setParamRows] = React.useState(
    () => getUrlParams(connString).map((p) => ({ name: p.key, value: p.value })),
  );
  const lastUrl = React.useRef(connString);
  React.useEffect(() => {
    if (connString !== lastUrl.current) {
      lastUrl.current = connString;
      setParamRows(getUrlParams(connString).map((p) => ({ name: p.key, value: p.value })));
    }
  }, [connString]);
  const setParams = (rows) => {
    setParamRows(rows);
    const url = setUrlParams(connString, rows.map((r) => ({ key: r.name || '', value: r.value })));
    lastUrl.current = url;
    setConnString(url);
  };

  const sectionLabel = {
    fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
    color: 'text.secondary',
  };

  return (
    <Stack spacing={2} sx={{ mt: 1.5 }}>
      {/* ---- Popular APIs (searchable preset picker) ---------------------- */}
      <Box>
        <Typography sx={{ ...sectionLabel, mb: 0.75 }}>Popular APIs</Typography>
        <Autocomplete
          size="small"
          options={REST_PRESETS}
          value={preset}
          filterOptions={presetFilter}
          groupBy={(p) => p.group}
          getOptionLabel={(p) => p.label}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, v) => {
            if (v) onApplyPreset(v);
            else setRest((r) => ({ ...r, presetId: null })); // keep the config, drop the badge
          }}
          // The popup must read as a separate, floating layer - elevated
          // solid surface, visible border + shadow, sticky tinted group
          // headers, and a clear hover/selected state on options.
          componentsProps={{
            paper: {
              elevation: 8,
              sx: {
                mt: 0.75,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                bgcolor: theme.palette.mode === 'dark' ? '#23262d' : theme.palette.background.paper,
                backgroundImage: 'none',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 12px 32px rgba(0,0,0,0.65)'
                  : '0 12px 32px rgba(0,0,0,0.18)',
                overflow: 'hidden',
                '& .MuiAutocomplete-listbox': {
                  py: 0,
                  maxHeight: 360,
                },
                '& .MuiAutocomplete-groupLabel': {
                  lineHeight: '30px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: theme.palette.primary.main,
                  bgcolor: theme.palette.mode === 'dark' ? '#1a1d23' : alpha(theme.palette.primary.main, 0.06),
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                '& .MuiAutocomplete-option': {
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  px: 1.5,
                  py: 0.9,
                  '&:last-of-type': { borderBottom: 'none' },
                  '&.Mui-focused, &:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.14),
                  },
                  '&[aria-selected="true"]': {
                    bgcolor: alpha(theme.palette.primary.main, 0.22),
                    '&.Mui-focused, &:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.28),
                    },
                  },
                },
                '& .MuiAutocomplete-noOptions': {
                  fontSize: 13,
                },
              },
            },
          }}
          renderOption={(props, p) => (
            <li {...props} key={p.id}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{p.label}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>
                  {p.auth?.type === 'none' ? 'No key needed' : 'Auth pre-configured'}
                  {p.incremental ? ' · historical + live' : ' · live polling'}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search 20 popular APIs — Alpaca, CoinGecko, FRED, Shopify…"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <TravelExploreIcon sx={{ fontSize: 18, color: 'text.secondary', ml: 0.5, mr: 0.5 }} />
                ),
                sx: { fontSize: 13 },
              }}
            />
          )}
        />
        {preset && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {preset.editHint}{' '}
            <Link href={preset.docs} target="_blank" rel="noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              API docs<OpenInNewIcon sx={{ fontSize: 12 }} />
            </Link>
          </Typography>
        )}
      </Box>

      {/* ---- smart-paste confirmation ------------------------------------ */}
      {smartPaste && (
        <Box sx={{
          px: 1.5, py: 1, borderRadius: 1.5,
          bgcolor: alpha(theme.palette.success.main, 0.08),
          border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <AutoFixHighIcon sx={{ fontSize: 18, color: 'success.main' }} />
          <Typography variant="caption" sx={{ color: 'success.main' }}>
            Imported from {smartPaste === 'curl' ? 'cURL' : 'a raw HTTP request'} — method, headers and
            auth were filled in below. Review and connect.
          </Typography>
        </Box>
      )}

      {/* ---- Method + Auth ------------------------------------------------ */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
        <TextField {...FIELD} select label="Method" value={rest.method || 'GET'}
          onChange={(e) => set({ method: e.target.value })} sx={{ ...FIELD.sx, maxWidth: { sm: 140 } }}>
          {['GET', 'POST'].map((m) => <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>{m}</MenuItem>)}
        </TextField>
        <TextField {...FIELD} select label="Authentication" value={rest.auth_type || 'none'}
          onChange={(e) => set({ auth_type: e.target.value })}>
          {AUTH_OPTIONS.map(([v, l]) => <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>{l}</MenuItem>)}
        </TextField>
      </Stack>

      {/* ---- Auth fields per scheme --------------------------------------- */}
      {rest.auth_type === 'bearer' && (
        <TextField {...FIELD} type="password" label="Bearer token" value={rest.token || ''}
          onChange={(e) => set({ token: e.target.value })}
          helperText="Sent as Authorization: Bearer <token>" />
      )}

      {rest.auth_type === 'apikey' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <TextField {...FIELD} select label="Send key in" value={rest.api_key_in || 'header'}
            onChange={(e) => set({ api_key_in: e.target.value })} sx={{ ...FIELD.sx, maxWidth: { sm: 160 } }}>
            <MenuItem value="header" sx={{ fontSize: 13 }}>Header</MenuItem>
            <MenuItem value="query" sx={{ fontSize: 13 }}>Query param</MenuItem>
          </TextField>
          <TextField {...FIELD} label={rest.api_key_in === 'query' ? 'Param name' : 'Header name'}
            placeholder={rest.api_key_in === 'query' ? 'api_key' : 'X-API-Key'}
            value={rest.api_key_name || ''} onChange={(e) => set({ api_key_name: e.target.value })} />
          <TextField {...FIELD} type="password" label="API key" value={rest.token || ''}
            onChange={(e) => set({ token: e.target.value })} />
        </Stack>
      )}

      {rest.auth_type === 'headers' && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            For APIs that need more than one secret header (e.g. Alpaca's key ID + secret key).
          </Typography>
          <KeyValueRows
            rows={rest.auth_headers || []}
            onChange={(rows) => set({ auth_headers: rows })}
            keyLabel="Header name" valueLabel="Secret value" secretValues
            addLabel="Add auth header"
          />
        </Box>
      )}

      {rest.auth_type === 'basic' && (
        <Stack direction="row" spacing={1.25}>
          <TextField {...FIELD} label="Username" value={rest.username || ''} onChange={(e) => set({ username: e.target.value })} />
          <TextField {...FIELD} type="password" label="Password" value={rest.password || ''} onChange={(e) => set({ password: e.target.value })} />
        </Stack>
      )}

      {rest.auth_type === 'oauth2_client_credentials' && (
        <Stack spacing={1.25}>
          <TextField {...FIELD} label="Token URL" placeholder="https://auth.provider.com/oauth/token"
            value={rest.oauth2?.token_url || ''}
            onChange={(e) => set({ oauth2: { ...rest.oauth2, token_url: e.target.value } })}
            helperText="We fetch and auto-refresh the access token before every sync." />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <TextField {...FIELD} label="Client ID" value={rest.oauth2?.client_id || ''}
              onChange={(e) => set({ oauth2: { ...rest.oauth2, client_id: e.target.value } })} />
            <TextField {...FIELD} type="password" label="Client secret" value={rest.oauth2?.client_secret || ''}
              onChange={(e) => set({ oauth2: { ...rest.oauth2, client_secret: e.target.value } })} />
            <TextField {...FIELD} label="Scope (optional)" value={rest.oauth2?.scope || ''}
              onChange={(e) => set({ oauth2: { ...rest.oauth2, scope: e.target.value } })} />
          </Stack>
        </Stack>
      )}

      {/* ---- Query params (synced with the URL) --------------------------- */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
          <Typography sx={sectionLabel}>Query params</Typography>
          <Tooltip title="These edit the URL above directly — the URL is always the source of truth.">
            <Typography variant="caption" color="text.secondary">(synced with the URL)</Typography>
          </Tooltip>
        </Stack>
        {paramRows.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            No query params yet — add one or just type them in the URL.
          </Typography>
        )}
        <KeyValueRows
          rows={paramRows}
          onChange={setParams}
          keyLabel="Param" valueLabel="Value" addLabel="Add param"
        />
      </Box>

      {/* ---- Extra headers ------------------------------------------------ */}
      <Box>
        <Typography sx={{ ...sectionLabel, mb: 0.75 }}>Extra headers (optional)</Typography>
        <KeyValueRows
          rows={rest.headers || []}
          onChange={(rows) => set({ headers: rows })}
          keyLabel="Header name" valueLabel="Value" addLabel="Add header"
        />
      </Box>

      {/* ---- POST body ----------------------------------------------------- */}
      {rest.method === 'POST' && (
        <TextField {...FIELD} multiline minRows={3} maxRows={8} label="Request body (JSON)"
          placeholder='{ "query": "..." }' value={rest.body || ''}
          onChange={(e) => set({ body: e.target.value })}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }} />
      )}
    </Stack>
  );
}

/**
 * RestSyncOptions - shown on the mapping step for REST sources.
 *
 * One decision, three answers: how should we capture this API?
 *   historical_live  Backfill from a start date, then keep syncing new data
 *                    (the watermark param turns each poll into "since X").
 *   live             Start from now; every poll only pulls fresh data.
 *   snapshot         One-time import of whatever the endpoint returns.
 */
export function RestSyncOptions({ sync, setSync, pagination, setPagination, theme }) {
  const MODES = [
    { id: 'historical_live', label: 'Historical + live', hint: 'Backfill history, then keep syncing' },
    { id: 'live', label: 'Live only', hint: 'Start from now' },
    { id: 'snapshot', label: 'One-time snapshot', hint: 'Import once, no schedule' },
  ];
  const canBackfill = !!(sync.param || '').trim();
  const set = (patch) => setSync((s) => ({ ...s, ...patch }));
  const setPg = (patch) => setPagination((p) => ({ ...p, ...patch }));

  return (
    <Box sx={{
      mt: 2, p: 2, borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`,
      bgcolor: theme.palette.background.paper,
    }}>
      <Typography sx={{
        fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
        color: 'text.secondary', mb: 1,
      }}>
        Data capture
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        {MODES.map((m) => {
          const active = sync.mode === m.id;
          const disabled = m.id === 'historical_live' && !canBackfill;
          return (
            <Box key={m.id} onClick={() => !disabled && set({ mode: m.id })}
              sx={{
                flex: 1, p: 1.25, borderRadius: 1.5, cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.45 : 1,
                border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
                bgcolor: active ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                transition: '0.12s',
              }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{m.label}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{m.hint}</Typography>
            </Box>
          );
        })}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 1.5 }}>
        <TextField {...FIELD} label="Time-window param"
          placeholder="start / since / startTime"
          value={sync.param || ''} onChange={(e) => set({ param: e.target.value })}
          helperText="The API's query param for 'data after X'. Powers backfill + incremental sync." />
        {sync.mode === 'historical_live' && (
          <TextField {...FIELD} label="Backfill from" type="date"
            value={sync.start || ''} onChange={(e) => set({ start: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="First sync pulls everything from this date forward." />
        )}
      </Stack>

      {!canBackfill && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          Provide the API's time-window param (e.g. Alpaca uses <code>start</code>) to unlock
          historical backfill. Without it we can still poll live snapshots.
        </Typography>
      )}

      {/* ---- Pagination (how we walk through large result sets) ----------- */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 1.5 }}>
        <TextField {...FIELD} select label="Pagination" value={pagination.type || 'none'}
          onChange={(e) => setPg({ type: e.target.value })} sx={{ ...FIELD.sx, maxWidth: { sm: 220 } }}
          helperText="How the API splits large results">
          <MenuItem value="none" sx={{ fontSize: 13 }}>None / single page</MenuItem>
          <MenuItem value="cursor" sx={{ fontSize: 13 }}>Cursor / next token</MenuItem>
          <MenuItem value="offset" sx={{ fontSize: 13 }}>Offset + limit</MenuItem>
          <MenuItem value="page" sx={{ fontSize: 13 }}>Page number</MenuItem>
          <MenuItem value="link" sx={{ fontSize: 13 }}>Link header (rel=next)</MenuItem>
        </TextField>
        {pagination.type === 'cursor' && (<>
          <TextField {...FIELD} label="Cursor param" placeholder="page_token"
            value={pagination.cursor_param || ''} onChange={(e) => setPg({ cursor_param: e.target.value })} />
          <TextField {...FIELD} label="Next-cursor path in response" placeholder="next_page_token"
            value={pagination.cursor_path || ''} onChange={(e) => setPg({ cursor_path: e.target.value })} />
        </>)}
        {pagination.type === 'offset' && (<>
          <TextField {...FIELD} label="Offset param" placeholder="offset"
            value={pagination.offset_param || ''} onChange={(e) => setPg({ offset_param: e.target.value })} />
          <TextField {...FIELD} label="Limit param" placeholder="limit"
            value={pagination.limit_param || ''} onChange={(e) => setPg({ limit_param: e.target.value })} />
        </>)}
        {pagination.type === 'page' && (<>
          <TextField {...FIELD} label="Page param" placeholder="page"
            value={pagination.page_param || ''} onChange={(e) => setPg({ page_param: e.target.value })} />
          <TextField {...FIELD} label="Page-size param" placeholder="page_size"
            value={pagination.page_size_param || ''} onChange={(e) => setPg({ page_size_param: e.target.value })} />
        </>)}
        {pagination.type !== 'none' && (
          <TextField {...FIELD} label="Page size" type="number" placeholder="100"
            value={pagination.page_size || ''} onChange={(e) => setPg({ page_size: e.target.value })}
            sx={{ ...FIELD.sx, maxWidth: { sm: 140 } }} />
        )}
      </Stack>
    </Box>
  );
}
